<template>
  <PrmButton
    v-if="disabled"
    v-tooltip="label"
    size="small"
    text
    disabled
    type="button"
    severity="secondary"
    class="navbar-icon"
  >
    <template #icon>
      <i
        v-if="isIconClass"
        :class="icon"
      />
      <component
        :is="icon"
        v-else
        class="navbar-icon__image"
      />
    </template>
  </PrmButton>
  <router-link
    v-else
    v-slot="{
      href, navigate, isActive,
    }"
    :to="routeTo"
    custom
  >
    <a
      :href="href"
      :aria-current="isActive ? 'page' : undefined"
      :target="openInNewTab ? '_blank' : undefined"
      :rel="openInNewTab ? 'noopener noreferrer' : undefined"
      class="button-nav"
      :class="{
        'button-nav--colored': color,
      }"
      :style="color
        ? {
          '--button-nav-color': color,
        }
        : undefined"
      @click="navigate"
    >
      <PrmButton
        v-tooltip="label"
        size="small"
        text
        type="button"
        tabindex="-1"
        :severity="isActive
          ? 'primary'
          : 'secondary'"
        :class="{
          'navbar-icon': !isActive || color,
          'button-nav__button--active': isActive,
        }"
      >
        <template #icon>
          <i
            v-if="isIconClass"
            :class="icon"
          />
          <component
            :is="icon"
            v-else
            class="navbar-icon__image"
          />
        </template>
      </PrmButton>
    </a>
  </router-link>
</template>

<script setup lang="ts">
import PrmButton from '@NitrexGroup/primevue/button';
import {
  computed,
} from 'vue';
import {
  RouteLocationRaw,
} from 'vue-router';

import type {
  TIcon,
} from '../../types/icon.ts';

const props = defineProps<{
  label: string,
  routeTo: RouteLocationRaw,
  icon: TIcon,
  disabled?: boolean,
  openInNewTab?: boolean,
  /**
   * Tints the hover, active and focus states, e.g. with the color of the app group.
   */
  color?: string,
}>();

const isIconClass = computed(() => {
  return typeof props.icon === 'string';
});
</script>

<style scoped lang="scss">
.navbar-icon {
  color: var(--text-color) !important;
}

// The link takes the keyboard focus, so the button inside it is left out of the tab order.
.button-nav {
  border-radius: var(--border-radius);
  outline: none;

  &:focus-visible .p-button {
    outline: 0 none;
    box-shadow: var(--focus-shadow);
  }
}

.button-nav--colored {
  .p-button.p-button-text {
    &:not(:disabled):hover {
      background: color-mix(in srgb, var(--button-nav-color) 15%, transparent);
    }

    &.button-nav__button--active,
    &.button-nav__button--active:not(:disabled):hover {
      background: color-mix(in srgb, var(--button-nav-color) 25%, transparent);
    }
  }

  &:focus-visible .p-button.p-button-text {
    background: color-mix(in srgb, var(--button-nav-color) 15%, transparent);
    box-shadow: 0 0 0 var(--focus-ring-width) var(--button-nav-color);
  }
}

.navbar-icon__image {
    min-width: 1.5rem;
    width: 1.5rem;
    height: 1.5rem;
  }
</style>
